import React from "react";

type ComponentModule<T> = { default: T };

/**
 * `React.lazy` that can be loaded ahead of time. Once its module has loaded, rendering it never
 * suspends, so navigating to it shows the page straight away instead of the Suspense fallback for a
 * frame (plain `React.lazy` suspends on its first render even when the chunk is already cached).
 *
 * A failed load is forgotten, so the next preload or render tries again.
 */
export function lazyWithPreload<T extends React.ComponentType<any>>(loadModule: () => Promise<ComponentModule<T>>) {
  let loadedModule: ComponentModule<T> | null = null;
  let loadingModule: Promise<ComponentModule<T>> | null = null;

  const preload = () => {
    if (!loadingModule) {
      loadingModule = loadModule().then(
        (componentModule) => {
          loadedModule = componentModule;
          return componentModule;
        },
        (error) => {
          loadingModule = null;
          throw error;
        }
      );
    }
    return loadingModule;
  };

  const LazyComponent = React.lazy(() => {
    if (!loadedModule) return preload();
    const componentModule = loadedModule;
    // React reads a lazy component synchronously when its thenable calls back synchronously.
    return { then: (resolve: (value: ComponentModule<T>) => void) => resolve(componentModule) } as Promise<
      ComponentModule<T>
    >;
  });

  return Object.assign(LazyComponent, { preload });
}
